<template>

    <v-container fluid v-if="sessionActive" class="pa-3" :key="'importer_'+importerKey">

        <material-header
            title="Panelist importer"
            info="Import panelist from CSV data sources"
            icon="mdi-progress-upload">

            <template slot="buttons">
                <v-scale-transition>
                    <v-tooltip color="primary" top>
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" v-show="getStepperModel > 1" color="primary" small dark fab @click="reset">
                                <v-icon>mdi-restart</v-icon>
                            </v-btn>
                        </template>
                        <span>Restart importer</span>
                    </v-tooltip>
                </v-scale-transition>
            </template>

        </material-header>

        <v-alert v-if="!hasPermission('import panelists')"
                 class="body-2"
                 type="error"
        >
            Your user account doesn't have permission to import panelists
        </v-alert>

        <v-stepper v-model="getStepperModel" v-if="hasPermission('import panelists')">
            <v-stepper-header>
                <v-stepper-step step="1" :complete="getStepperModel > 1">Select file to import
                </v-stepper-step>

                <v-divider></v-divider>
                <v-stepper-step class="cursor-pointer" @click="stepperClicked(2)" step="2"
                                :complete="getStepperModel > 2">Inspection
                </v-stepper-step>
                <v-divider></v-divider>
                <v-stepper-step class="cursor-pointer" @click="stepperClicked(3)" step="3"
                                :complete="getStepperModel > 3">Mapping
                </v-stepper-step>
                <v-divider></v-divider>
                <v-stepper-step class="cursor-pointer" @click="stepperClicked(4)" step="4"
                                :complete="getStepperModel > 4">Upload
                </v-stepper-step>
                <v-divider></v-divider>
                <v-stepper-step step="5">Results</v-stepper-step>
            </v-stepper-header>

            <v-stepper-items>

                <v-stepper-content step="1" class="pa-0">
                    <v-card>
                        <v-card-text>
                            <v-row dense>
                                <v-col cols="10">
                                    <v-alert
                                        type="info"
                                        class="body-2"
                                    >
                                        CSV Import files must contains columns for FIRST_NAME, LAST_NAME and
                                        EMAIL Data Variables
                                    </v-alert>

                                </v-col>

                                <v-col cols="2">
                                    <help-icon class="mt-2 text-right" help="panelist-imported-select-file"></help-icon>
                                </v-col>

                                <v-col cols="12" class="pt-3 pb-3">

                                    <div class="dropzone"
                                         @drop.prevent="dropHandler"
                                         @dragover.prevent="dragoverHandler"
                                         @dragend.prevent="dragendHandler">

                                        <label class="file-select">
                                            <div class="select-button" style="height: 100%">
                                                <strong>Drop a CSV file into this box OR click here to select
                                                    one</strong>
                                            </div>
                                            <input type="file"
                                                   style="border: 1px solid yellow"
                                                   ref="fileInput"
                                                   @change="fileSelectedHandler"/>

                                        </label>
                                    </div>
                                </v-col>

                                <v-col cols="12">

                                    <v-alert
                                        class="body-2"
                                        type="info"
                                        color="grey darken-2"
                                    >
                                        Additionally you may import the various system variables. Click on the help button to learn more!
                                    </v-alert>
                                </v-col>

                                <v-col cols="12">
                                    <v-btn text @click="importFileGeneratorDialogModel=true">Generate test import file
                                    </v-btn>
                                </v-col>

                            </v-row>

                        </v-card-text>
                    </v-card>
                </v-stepper-content>

                <!-- File inspector -->
                <v-stepper-content step="2">

                    <file-inspector
                        class="pb-10"
                        :headers="fileHeaders"
                        :key="fileInspectorKey"
                        :data="tableData"
                        :error-lines="fileErrors"
                        :loading="fileParsing"
                        @reset="reset"
                        @complete="fileInspectorCompleted"
                    ></file-inspector>

                </v-stepper-content>

                <!-- CSV Column Mapper -->
                <v-stepper-content step="3">

                    <column-mapper
                        class="pt-2"
                        v-if="fileInspectorDone && dataVariables.length > 0"
                        :key="columnMapperKey"
                        :data="fileData"
                        :headers="fileHeaders"
                        :data-variables="dataVariables"
                        @reset="reset"
                        @complete="columnMapperCompleted"
                    >
                    </column-mapper>

                </v-stepper-content>

                <!-- Upload -->
                <v-stepper-content step="4">

                    <uploader
                        v-if="columnMapperDone"
                        :key="uploaderKey"
                        :file="file"
                        @reset="reset"
                        @complete="uploaderCompleted"
                        @back="backClicked"
                    ></uploader>

                </v-stepper-content>

                <!-- Results -->
                <v-stepper-content step="5">

                    <results
                        v-if="uploaderDone"
                        :key="resultsKey"
                        :file="file"
                        @reset="reset"
                        @back="backClicked"
                    ></results>

                </v-stepper-content>
            </v-stepper-items>

        </v-stepper>

        <confirmation-dialog
            v-model="confirmationDialogModel"
            headline="Reset confirmation"
            description="The current mappings and selections will be lost if you create a new import"
            confirm-button-text="Reset"
            @selected="confirmAction"
        ></confirmation-dialog>

        <data-variable-picker v-model="importFileGeneratorDialogModel"
                              :data-variables="importableVariables"
                              @close="dialog=false"
                              multiple
                              :selected="defaultVariables"
                              @change="variablesSelected">
        </data-variable-picker>

    </v-container>

</template>

<script>

import {mapState, mapMutations, mapGetters, mapActions} from 'vuex';

import Uploader from './Uploader';
import FileInspector from './FileInspector';
import ColumnMapper from './ColumnMapper';
import Store from '../store';
import activeSessionChecker from '../../../components/mixins/activeSessionCheckerMixin';
import MaterialHeader from '../../../components/UI/MaterialHeader';
import * as Papa from 'papaparse';
import Results from "./Results";
import ConfirmationDialog from "../../../components/UI/ConfirmationDialog";
import DataVariablePicker from "../../../components/UI/DataVariablePicker";
import HelpIcon from "../../../components/UI/HelpIcon";

const vuexModule = 'PanelistImporter';
const REQUIRED_HEADERS = ['EMAIL','FIRST_NAME','LAST_NAME'];

export default {

    name: 'PanelistImporter',

    mixins: [activeSessionChecker],

    components: {
        HelpIcon,
        MaterialHeader,
        Uploader,
        FileInspector,
        ColumnMapper,
        Results,
        ConfirmationDialog,
        DataVariablePicker
    },

    data() {
        return {

            // Keep file inside the component
            // Vuex store is slow for big files!
            fileParsing: false,
            file: null,
            fileData: [],
            fileHeaders: [],
            fileErrors: [],
            confirmationDialogModel: false,
            importFileGeneratorDialogModel: false,
            importerKey: 1,
        }
    },

    created() {
        if (!this.$store.state.vuexModule) {

            // Register Vuex store for the component
            this.$store.registerModule(vuexModule, Store);
        }
    },

    mounted() {

        // These are needed in the upload screen
        this.$store.dispatch('getDataVariables');
        this.$store.dispatch('RecruitmentSources/loadRecruitmentSources');
        this.$store.dispatch('SubPanels/loadSubPanels');
    },

    computed: {

        ...mapState(['dataVariables']),

        importableVariables() {
            return this.dataVariables.filter(variable => variable.panelist_writable);
        },

        defaultVariables() {
            return this.dataVariables.filter(variable => REQUIRED_HEADERS.includes(variable.name));
        },

        tableData() {
            return this.fileData.slice(0, 100);
        },

        showResetConfirmation() {
            return this.stepperModel > 2;
        },

        getStepperModel: {
            get() {
                return this.stepperModel;
            },
            set(stepperModel) {
                this.setStepperModel(stepperModel);
            }
        },

        ...mapState([
            'dataVariables',
            'dataVariableTypes'
        ]),

        ...mapState(vuexModule, [
            'userMap',
            'stepperModel',
            'fileInspectorDone',
            'columnMapperDone',
            'uploaderDone',
            'fileInspectorKey',
            'columnMapperKey',
            'uploaderKey',
            'resultsKey',
        ]),
    },

    methods: {
        ...mapMutations(vuexModule, [
            'setStepperModel',
            'setFileInspectorDone',
            'setColumnMapperDone',
            'setUploaderDone'
        ]),

        // Root store
        ...mapActions([
            'getDataVariables'
        ]),

        ...mapActions(vuexModule, [
            'resetState',
            'requestTestFile'
        ]),

        variablesSelected(variables) {
            this.requestTestFile(variables)
                .then(() => {
                    Bus.$emit('flash-message', {message: 'File is generating and will be placed in the downloads'});
                })
        },

        stepperClicked($step) {
            if (this.stepperModel > $step) this.setStepperModel($step);
        },

        confirmAction(action) {
            if (action) this.resetDo();
        },

        reset() {

            this.importerKey++;

            if (this.showResetConfirmation) {
                this.confirmationDialogModel = true;
            } else {
                this.resetDo();
            }
        },

        resetDo() {
            this.resetState();

            // Reset uploaded file
            this.file = null;
            this.fileData = [];
            this.fileHeaders = [];
            this.fileErrors = [];

            // Clear file input on reset
            this.$refs.fileInput.value = '';
        },

        backClicked() {
            this.setStepperModel(this.stepperModel - 1);
        },

        fileInspectorCompleted() {

            this.setFileInspectorDone(true);
            this.goToPage(3);
        },

        columnMapperCompleted() {
            this.setColumnMapperDone(true);
            this.goToPage(4);
        },

        uploaderCompleted() {
            this.setUploaderDone(true);
            this.goToPage(5);
        },

        goToPage(page) {
            this.setStepperModel(page);
            window.scrollTo(0, 0);
        },

        findHeaderIndex(data) {
            for (let index = 0; index < 10; index++) {

                const csvHeaders = data[index];

                const found = REQUIRED_HEADERS.filter(header => {
                    return csvHeaders.find(name => REQUIRED_HEADERS.includes(name.toUpperCase()));
                });

                if (found.length === REQUIRED_HEADERS.length) return index;
            }
            return null;
        },

        readFile(file) {

            this.fileParsing = true;

            Papa.parse(file, {
                worker: true,
                delimiter: ",",	// auto-detect
                newline: "",	// auto-detect
                quoteChar: '"',
                comments: '#',
                header: false,
                dynamicTyping: true,
                preview: 0,  // Limit number of rows parsed
                encoding: "",
                skipEmptyLines: true,

                complete: (results) => {

                    const headerIndex = this.findHeaderIndex(results.data);

                    if (headerIndex === null) {
                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: 'CSV header appears to be missing or is unrecognizable. ' +
                            'Failed to locate one or more of the required headers ' +
                            REQUIRED_HEADERS.join(', ')
                        });

                        this.fileParsing = false;
                        return;
                    }

                    this.goToPage(2);

                    this.file = file;
                    this.fileHeaders = results.data[headerIndex].map((header) => {

                        const parts = header.split(":");

                        if (parts.length === 2) {
                            return parts[0].trim() + ":" + parts[1].trim();
                        } else {
                            return header.trim();
                        }
                    });

                    // Remove header row from data
                    results.data.shift();

                    this.fileData = results.data;
                    this.fileParsing = false;

                    // TODO: remove this when fixed
                    // Due to PapaParse bug it is not currently detecting missing or extra columns in data
                    // so we do it manually here.
                    this.fileData.forEach((items, index) => {

                        if (index > headerIndex) {

                            if (items.length < this.fileHeaders.length) {
                                results.errors.push({
                                    type: 'FieldMismatch',
                                    code: 'TooFewFields',
                                    message: 'Line contains ' + items.length + ' fields but should have ' + this.fileHeaders.length + ' fields',
                                    row: index
                                });
                            }

                            if (items.length > this.fileHeaders.length) {
                                results.errors.push({
                                    type: 'FieldMismatch',
                                    code: 'TooManyFields',
                                    message: 'Line contains ' + items.length + ' fields but should have ' + this.fileHeaders.length + ' fields',
                                    row: index
                                });
                            }
                        }
                    });

                    if (results.errors.length > 0) {
                        this.handleErrors(results.errors);
                    }
                }
            });
        },

        handleErrors(errorData) {

            errorData.forEach((error) => {

                let unParseConfig = {
                    'quotes': true,
                    'delimiter': ', '
                };

                const errorLine = this.fileData.slice(error.row, error.row + 1);

                // Convert back to CSV
                let csv = Papa.unparse(errorLine, unParseConfig);

                this.fileErrors.push({
                    lineNumber: error.row + 1,
                    type: error.type,
                    message: error.message,
                    line: csv
                });
            });
        },

        /**
         * File drop zone drop event handler
         * @param event
         */
        dropHandler(event) {

            let dataTransfer = event.dataTransfer;

            if (dataTransfer.items) {

                // Error out if we got folder or anything else
                if (dataTransfer.items[0].kind !== "file") {

                    Bus.$emit("flash-message", {
                        type: 'error',
                        message: 'Invalid item dropped. Only CSV files are mappingsDone'
                    });
                }

                const file = dataTransfer.items[0].getAsFile();

                if (this.isExtensionValid(file)) {
                    this.readFile(file);
                }
            }
        },

        /**
         *  Drag over event for Drop Zone
         * @param event
         */
        dragoverHandler(event) {
        },

        /**
         * Drag end event for Drop Zone
         * @param event
         */
        dragendHandler(event) {
        },


        /**
         * File selected handler
         * @param event
         */
        fileSelectedHandler(event) {
            const file = event.target.files[0];

            if (this.isExtensionValid(file)) {
                this.readFile(file);
            }
        },

        /**
         * Validate extension, save file and move to the next screen
         * @param file
         */
        isExtensionValid(file) {
            if (file.name.split('.').pop() === 'csv') {
                return true;
            }

            Bus.$emit("flash-message", {
                type: 'error',
                message: 'You didn\'t read the instructions. Only CSV files are mappingsDone!'
            });

            return false;
        },
    },
}

</script>

<style scoped>
.dropzone {
    border: 2px dashed gray;
    padding: 20px;
    height: 200px;
}

.file-select > input[type="file"] {
    display: none;
}
</style>
